/**
 * MCP client — communicates with the Convex HTTP Action gateway.
 *
 * Two configuration modes:
 *
 * 1. **Cloud mode** (single key):
 *    PRODUCTBRAIN_API_KEY=pb_sk_...
 *    The cloud URL is resolved automatically. Workspace is inferred from the key.
 *
 * 2. **Self-hosted mode** (three vars):
 *    CONVEX_SITE_URL  — base URL of the Convex deployment (*.convex.site)
 *    MCP_API_KEY      — secret key matching MCP_API_KEY set in the Convex dashboard env vars
 *    WORKSPACE_SLUG   — slug of the workspace to operate on
 */

import { trackToolCall } from "./analytics.js";

const DEFAULT_CLOUD_URL = "https://earnest-sheep-635.convex.site";

let cachedWorkspaceId: string | null = null;
let cloudMode = false;

export interface AuditEntry {
  ts: string;
  fn: string;
  workspace: string;
  status: 'ok' | 'error';
  durationMs: number;
  error?: string;
}

const AUDIT_BUFFER_SIZE = 50;
const auditBuffer: AuditEntry[] = [];

/**
 * Bootstrap cloud mode: if PRODUCTBRAIN_API_KEY is set, map it to the
 * internal env vars so the rest of the client works transparently.
 */
export function bootstrapCloudMode(): void {
  const pbKey = process.env.PRODUCTBRAIN_API_KEY;
  if (pbKey?.startsWith("pb_sk_")) {
    const cloudUrl = process.env.PRODUCTBRAIN_URL ?? DEFAULT_CLOUD_URL;
    process.env.CONVEX_SITE_URL ??= cloudUrl;
    process.env.MCP_API_KEY ??= pbKey;
    cloudMode = true;
  }
}

function getEnv(key: string): string {
  const value = process.env[key];
  if (!value) throw new Error(`${key} environment variable is required`);
  return value;
}

/** Only write audit to stderr on errors, or when MCP_DEBUG=1. Keeps default DX quiet. */
function shouldLogAudit(status: 'ok' | 'error'): boolean {
  return status === 'error' || process.env.MCP_DEBUG === '1';
}

function audit(fn: string, status: 'ok' | 'error', durationMs: number, errorMsg?: string): void {
  const ts = new Date().toISOString();
  const workspace = cachedWorkspaceId ?? 'unresolved';

  const entry: AuditEntry = { ts, fn, workspace, status, durationMs };
  if (errorMsg) entry.error = errorMsg;
  auditBuffer.push(entry);
  if (auditBuffer.length > AUDIT_BUFFER_SIZE) auditBuffer.shift();

  trackToolCall(fn, status, durationMs, workspace, errorMsg);

  if (!shouldLogAudit(status)) return;

  const base = `[MCP-AUDIT] ${ts} fn=${fn} workspace=${workspace} status=${status} duration=${durationMs}ms`;
  if (status === 'error' && errorMsg) {
    process.stderr.write(`${base} error=${JSON.stringify(errorMsg)}\n`);
  } else {
    process.stderr.write(`${base}\n`);
  }
}

/** Returns a snapshot of the audit ring buffer (most recent last). */
export function getAuditLog(): readonly AuditEntry[] {
  return auditBuffer;
}

/**
 * Low-level call to the HTTP Action gateway. Does NOT inject workspaceId.
 * Use mcpQuery / mcpMutation for workspace-scoped calls.
 */
export async function mcpCall<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const siteUrl = getEnv('CONVEX_SITE_URL').replace(/\/$/, '');
  const apiKey = getEnv('MCP_API_KEY');

  const start = Date.now();

  let res: Response;
  try {
    res = await fetch(`${siteUrl}/api/mcp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ fn, args }),
    });
  } catch (err: any) {
    audit(fn, 'error', Date.now() - start, err.message);
    throw new Error(`MCP call "${fn}" network error: ${err.message}`);
  }

  const json = await res.json() as { data?: T; error?: string };

  if (!res.ok || json.error) {
    audit(fn, 'error', Date.now() - start, json.error);
    throw new Error(`MCP call "${fn}" failed (${res.status}): ${json.error ?? 'unknown error'}`);
  }

  audit(fn, 'ok', Date.now() - start);
  return json.data as T;
}

/** Resolve and cache the workspace ID. Cloud mode resolves from the API key; self-hosted uses WORKSPACE_SLUG. */
export async function getWorkspaceId(): Promise<string> {
  if (cachedWorkspaceId) return cachedWorkspaceId;

  if (cloudMode) {
    // Cloud keys: the backend resolves workspace from the key itself
    const workspace = await mcpCall<{ _id: string; name: string; slug: string } | null>(
      'resolveWorkspace',
      { slug: "__cloud__" },
    );
    if (!workspace) {
      throw new Error("Cloud key is valid but no workspace is associated. Run `npx productbrain setup` again.");
    }
    cachedWorkspaceId = workspace._id;
    return cachedWorkspaceId;
  }

  const slug = getEnv('WORKSPACE_SLUG');
  const workspace = await mcpCall<{ _id: string; name: string; slug: string } | null>(
    'resolveWorkspace',
    { slug },
  );

  if (!workspace) {
    throw new Error(`Workspace with slug "${slug}" not found`);
  }

  cachedWorkspaceId = workspace._id;
  return cachedWorkspaceId;
}

/** Call a query scoped to the current workspace. */
export async function mcpQuery<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const workspaceId = await getWorkspaceId();
  return mcpCall<T>(fn, { ...args, workspaceId });
}

/** Call a mutation scoped to the current workspace. */
export async function mcpMutation<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const workspaceId = await getWorkspaceId();
  return mcpCall<T>(fn, { ...args, workspaceId });
}
